import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Plus, Settings, Shield } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { EmptyPlaceholder } from '@/components/EmptyPlaceholder';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess } from '@/context/auth/types';
import { useFetchTriggers } from '@/hooks/sms/useTriggers';
import TriggerList from '@/components/sms/triggers/TriggerList';
import TriggerForm from '@/components/sms/triggers/TriggerForm';
import TriggerTemplates from '@/components/sms/triggers/TriggerTemplates';

interface TriggersTabProps {
  onError?: (error: Error) => void;
}

type ViewMode = 'list' | 'create' | 'edit' | 'templates';

const TriggersTab: React.FC<TriggersTabProps> = ({ onError }) => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [selectedTriggerId, setSelectedTriggerId] = useState<string | null>(null);
  
  const { triggers, loading, error, refetch } = useFetchTriggers(user);

  // Check if user has access to triggers functionality
  const hasAccess = user && (
    hasSuperAdminAccess(user.role) || 
    hasOrgAdminAccess(user.role)
  );

  // Access denied screen
  if (!hasAccess) {
    return (
      <div className="py-8">
        <Card className="max-w-lg mx-auto">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <Shield className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-medium">Access Restricted</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  SMS triggers are only available to organisation administrators and super administrators.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCreateTrigger = () => {
    setCurrentView('create');
    setSelectedTriggerId(null);
  };

  const handleEditTrigger = (triggerId: string) => {
    setSelectedTriggerId(triggerId);
    setCurrentView('edit');
  };

  const handleViewTemplates = () => {
    setCurrentView('templates');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedTriggerId(null);
    refetch(); // Refresh triggers when returning to list
  };

  const handleTriggerCreated = () => {
    setCurrentView('list');
    refetch();
  };

  const handleTriggerUpdated = () => {
    setCurrentView('list');
    refetch();
  };

  const handleTemplateSelected = () => {
    setCurrentView('create');
  };

  // Render form views
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="py-4 space-y-6 overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-medium">
              {currentView === 'create' ? 'Create Trigger' : 'Edit Trigger'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {currentView === 'create' 
                ? 'Set up automated SMS messages based on customer behavior'
                : 'Modify trigger settings and conditions'
              }
            </p>
          </div>
          <Button variant="outline" onClick={handleBackToList}>
            Back to Triggers
          </Button>
        </div>
        
        <Separator />
        
        <TriggerForm
          triggerId={selectedTriggerId}
          onSuccess={currentView === 'create' ? handleTriggerCreated : handleTriggerUpdated}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Render templates view
  if (currentView === 'templates') {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Trigger Templates</h3>
            <p className="text-sm text-muted-foreground">
              Choose from pre-built trigger templates to get started quickly
            </p>
          </div>
          <Button variant="outline" onClick={handleBackToList}>
            Back to Triggers
          </Button>
        </div>
        
        <Separator />
        
        <TriggerTemplates
          onTemplateSelect={handleTemplateSelected}
          onCreateBlank={handleCreateTrigger}
        />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Triggers</h3>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleViewTemplates}>
              Templates
            </Button>
            <Button onClick={handleCreateTrigger} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Trigger
            </Button>
          </div>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load triggers: {error}
          </AlertDescription>
        </Alert>

        <div className="flex justify-center">
          <Button variant="outline" onClick={refetch}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Empty state - no triggers
  if (!loading && triggers.length === 0) {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Triggers</h3>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleViewTemplates}>
              Templates
            </Button>
            <Button onClick={handleCreateTrigger} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Trigger
            </Button>
          </div>
        </div>

        <EmptyPlaceholder
          title="No triggers configured"
          description="Create your first SMS trigger to automatically engage customers based on their behavior."
          icon={<Settings className="h-12 w-12" />}
        >
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleViewTemplates}>
              Browse Templates
            </Button>
            <Button onClick={handleCreateTrigger}>
              Create Trigger
            </Button>
          </div>
        </EmptyPlaceholder>

        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">Why Use SMS Triggers?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Automated Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Send personalized messages automatically based on customer actions and milestones.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Increase Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Re-engage dormant customers and encourage repeat orders with targeted messaging.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Save Time</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Set up once and let triggers run automatically, reaching customers at the right time.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Main triggers list view
  return (
    <div className="py-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Triggers</h3>
          <p className="text-sm text-muted-foreground">
            Manage automated SMS campaigns triggered by customer behavior
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleViewTemplates}>
            Templates
          </Button>
          <Button onClick={handleCreateTrigger} className="gap-1">
            <Plus className="h-4 w-4" />
            Create Trigger
          </Button>
        </div>
      </div>

      <Separator />

      <TriggerList
        triggers={triggers}
        loading={loading}
        onEdit={handleEditTrigger}
        onRefresh={refetch}
      />
    </div>
  );
};

export default TriggersTab;